'use client';

import { useState, useEffect } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { checkAuth } from "../../lib/auth";

export default function Contact() {
  const pathname = usePathname();
  const currentLocale = pathname.match(/^\/([a-z]{2}-[A-Z]{2})/)?.[1] || 'zh-CN';
  const t = useTranslations('contact');
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState("");
  const { isAuthenticated, user } = checkAuth();
  const [settings, setSettings] = useState({
    siteName: '企业名称',
    siteEmail: 'info@example.com',
    sitePhone: '400-123-4567',
    siteAddress: '北京市朝阳区某某大厦'
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings');
        if (!response.ok) {
          throw new Error('Failed to fetch settings');
        }
        const data = await response.json();
        setSettings(data);
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };

    fetchSettings();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitSuccess(false);

    try {
      const response = await fetch('/api/consultations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit consultation');
      }

      setSubmitSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
    } catch (err) {
      console.error('Error submitting consultation:', err);
      setError('提交失败，请稍后重试');
    }
  };

  return (
    <div>
      {/* 页面标题 */}
      <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center text-black">{t('title')}</h1>
          <p className="text-center text-black mt-4">{t('description')}</p>
        </div>
      </section>

      {/* 联系表单 */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-black">{t('title')}</h2>
              <p className="text-black mb-8">
                填写以下表单，我们将在24小时内回复您。
              </p>
              
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 p-4 rounded-lg mb-6">
                  {error}
                </div>
              )}
              
              {submitSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 p-4 rounded-lg mb-6">
                  提交成功！我们将尽快与您联系。
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-black mb-1">{t('form.name')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-black mb-1">{t('form.email')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-black mb-1">{t('form.phone')}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-black mb-1">{t('form.subject')}</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-black mb-1">{t('form.message')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  {t('form.submit')}
                </button>
              </form>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6 text-black">{t('info.title')}</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-blue-600 text-2xl">📞</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('info.phone')}</h3>
                    <p className="text-black">{settings.sitePhone}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-blue-600 text-2xl">✉️</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('info.email')}</h3>
                    <p className="text-black">{settings.siteEmail}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-blue-600 text-2xl">📍</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('info.address')}</h3>
                    <p className="text-black">{settings.siteAddress}</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4 text-black">工作时间</h3>
                <p className="text-black mb-2">周一至周五: 9:00 - 18:00</p>
                <p className="text-black">周六至周日: 休息</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}